import { Component, Input, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { ElementDetailsFragment } from "@/core/graphql/graphql";
import { FormControl } from "@angular/forms";
import { FeaturedElementsDeprecatedService } from "@/core/services/featured-elements-deprecated.service";
import { combineLatest, Subscription } from "rxjs";
import { first, map, tap } from "rxjs/operators";
import { OnboardingDeprecatedService } from "@/core/services/onboarding-deprecated.service";
import { MatDialogRef } from "@angular/material/dialog";
import { CosmosDialogService, CosmosDialogStyle } from "@/core/services/cosmos-dialog.service";
import { UserDetailsModalComponent } from "@/modules/users/components/user-details-modal/user-details-modal.component";
import { AccordionItem } from "@/shared/components/accordion/expandable-panel.component";

@Component({
  selector: "cosmos-element-summary",
  styleUrls: ["./element-summary.component.scss"],
  templateUrl: "./element-summary.component.html",
})
export class ElementSummaryComponent implements OnDestroy {
  @ViewChild('isFeaturedSlider', { static: true }) public featuredSlider: TemplateRef<any>;
  @ViewChild('isOnboardedSlider', { static: true }) public onboardedSlider: TemplateRef<any>;
  @ViewChild('url', { static: true }) public url: TemplateRef<any>;
  @ViewChild('userDetailsButton', { static: true }) public ownerButton: TemplateRef<any>;
  @ViewChild('sourceLink', { static: true }) public sourceLink: TemplateRef<any>;
  @Input() public set element(element: ElementDetailsFragment) {
    if (element) {
      this._element = element;
      this.loading = false;
      this.setupFormControl();
    }
  }

  public get element(): ElementDetailsFragment {
    return this._element;
  }

  public loading: boolean;
  public isFeatured: FormControl;
  public isOnboarded: FormControl;
  public accordionData: AccordionItem[];

  private readonly _subscription: Subscription = new Subscription();
  private _element: ElementDetailsFragment;

  public constructor(
    private readonly _featuredElementsService: FeaturedElementsDeprecatedService,
    private readonly _dialog: CosmosDialogService,
    private readonly _onboardingService: OnboardingDeprecatedService,
    private readonly _modalRef: MatDialogRef<ElementSummaryComponent>,
  ) { }

  private setupFormControl(): void {
    const isFeatured$ = this._featuredElementsService.getFeaturedElements()
      .pipe(
        map((elements) => {
          const indexesArray = elements.map(element => element.id);
          return indexesArray.includes(this._element.id);
        }),
        first(),
        tap(x => this.isFeatured = new FormControl(x)),
      );

    const isOnboarded$ = this._onboardingService.getFeaturedElements()
      .pipe(
        map((elements) => {
          const indexesArray = elements.map(element => element.id);
          return indexesArray.includes(this._element.id);
        }),
        first(),
        tap(x => this.isOnboarded = new FormControl(x)),
      );

    this._subscription.add(
      combineLatest([isFeatured$, isOnboarded$])
        .subscribe(() => this.handleFormControl()),
    );

    this.accordionData = [
      { name: 'Type: ', value: this._element.type },
      { name: 'Is Processed: ', value: this._element.isProcessed },
      { name: 'ID: ', value: this.element.id },
      { name: 'Connection Count: ', value: this._element.numberOfConnections },
      { name: 'Owner Id: ', value: this._element.ownerId, template: this.ownerButton },
      { name: 'Share url: ', value: this._element.url, template: this.url },
      { name: 'Is featured: ', value: false, template: this.featuredSlider },
      { name: 'Is onboarded: ', value: false, template: this.onboardedSlider },
      { name: 'Source: ', value: this._element.url, template: this.sourceLink },
    ];
  }

  private handleFormControl(): void {
    if (this.isFeatured) {
      this._subscription.add(
        this.isFeatured.valueChanges.subscribe(next => {
          next
            ? this._featuredElementsService.addFeaturedElement(this._element.id)
            : this._featuredElementsService.removeFeaturedElement(this._element.id);
        }),
      );
    }

    if (this.isOnboarded) {
      this._subscription.add(
        this.isOnboarded.valueChanges.subscribe(next => {
          next
            ? this._onboardingService.addFeaturedElement(this._element.id)
            : this._onboardingService.removeFeaturedElement(this._element.id);
        }),
      );
    }
  }

  public openOwnerDetails(): void {
    this._dialog.open<UserDetailsModalComponent, { userId: number }>(
      UserDetailsModalComponent,
      {
        data: {
          userId: this._element.ownerId,
        },
        style: CosmosDialogStyle.Dark,
      });
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
